import { RECURRENCE_FREQS, RECURRENCE_INTERVAL_MAX, type Recurrence } from '@contracts/assistant-proactive';
import type { ISODate } from '@contracts/common';
import {
  addDays, BUSINESS_UTC_OFFSET, businessDate, businessInstant, daysBetween, daysInMonth, DomainRuleError, isoWeekday,
  isWorkday, parseDate,
} from './dates';

/**
 * S3 L2 §7.6 / D67: when a reminder fires. Every occurrence is counted from the anchor — the local
 * Asia/Ho_Chi_Minh date and time the user set (`recurrence_anchor_local`, written 'YYYY-MM-DDTHH:MM') — never from
 * the last `next_at` or from when the worker ran, so a clamped month (31/01 → 28/02) goes back to 31/03 and a long
 * downtime only skips ahead. The series is cut into periods k = 0, 1, 2 … (a day, a week or a month, `interval`
 * apart), each holding zero or more local days that all share the anchor's local time.
 */

const LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/;
const HOUR_MS = 3_600_000;
/** More consecutive empty periods than this means the series never fires again (e.g. every 7 days from a Saturday, workdays only). */
const MAX_EMPTY_PERIODS = 8;
const KEYS = new Set(['freq', 'interval', 'byWeekday', 'workdaysOnly', 'until']);

type Local = { date: ISODate; time: string };

function invalid(message: string): never {
  throw new DomainRuleError('INVALID_RECURRENCE', message, 'recurrence');
}

function parseLocal(local: string): Local {
  const match = typeof local === 'string' ? LOCAL.exec(local) : null;
  if (!match) throw new DomainRuleError('INVALID_DATE', `thời điểm phải có dạng YYYY-MM-DDTHH:MM: ${String(local)}`);
  businessInstant(match[1]!, match[2]!); // validates both parts
  return { date: match[1]!, time: match[2]! };
}

/** The instant of a local business time 'YYYY-MM-DDTHH:MM'. */
export function localToInstant(local: string): Date {
  const { date, time } = parseLocal(local);
  return businessInstant(date, time);
}

/** The local business time 'YYYY-MM-DDTHH:MM' of an instant; seconds are dropped. */
export function instantToLocal(at: Date): string {
  const date = businessDate(at);
  const shifted = new Date(at.getTime() + Number(BUSINESS_UTC_OFFSET.slice(0, 3)) * HOUR_MS);
  const hh = String(shifted.getUTCHours()).padStart(2, '0');
  const mm = String(shifted.getUTCMinutes()).padStart(2, '0');
  return `${date}T${hh}:${mm}`;
}

/**
 * Checks an untrusted recurrence and returns it normalised (`byWeekday` sorted). `byWeekday` (ISO 1 = Monday …
 * 7 = Sunday) belongs to weekly only and `workdaysOnly` to daily only. With the anchor it also refuses an `until`
 * before the anchor's day and a series that would never fire.
 */
export function validateRecurrence(value: unknown, anchorLocal?: string): Recurrence {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) invalid('lịch lặp phải là một đối tượng');
  const raw = value as Record<string, unknown>;
  for (const key of Object.keys(raw)) if (!KEYS.has(key)) invalid(`lịch lặp có trường lạ: ${key}`);
  const freq = raw.freq;
  if (!(RECURRENCE_FREQS as readonly unknown[]).includes(freq)) invalid(`kiểu lặp không hợp lệ: ${String(freq)}`);
  const interval = raw.interval;
  if (!Number.isInteger(interval) || (interval as number) < 1 || (interval as number) > RECURRENCE_INTERVAL_MAX) {
    invalid(`khoảng lặp phải là số nguyên 1–${RECURRENCE_INTERVAL_MAX}`);
  }
  const out: Recurrence = { freq: freq as Recurrence['freq'], interval: interval as number };
  if (raw.byWeekday !== undefined) {
    if (freq !== 'weekly') invalid('chỉ lịch lặp hằng tuần mới chọn được thứ');
    const days = raw.byWeekday;
    if (!Array.isArray(days) || days.length === 0) invalid('danh sách thứ không được rỗng');
    if (days.some((d) => !Number.isInteger(d) || d < 1 || d > 7)) invalid('thứ phải là số 1 (thứ 2) đến 7 (chủ nhật)');
    if (new Set(days).size !== days.length) invalid('danh sách thứ bị lặp');
    out.byWeekday = [...(days as number[])].sort((a, b) => a - b);
  }
  if (raw.workdaysOnly !== undefined) {
    if (freq !== 'daily') invalid('chỉ lịch lặp hằng ngày mới bỏ được ngày nghỉ');
    if (typeof raw.workdaysOnly !== 'boolean') invalid('workdaysOnly phải là đúng/sai');
    out.workdaysOnly = raw.workdaysOnly;
  }
  if (raw.until !== undefined) {
    if (typeof raw.until !== 'string') invalid('ngày kết thúc phải có dạng YYYY-MM-DD');
    try { out.until = parseDate(raw.until); } catch { invalid(`ngày kết thúc không hợp lệ: ${raw.until}`); }
  }
  if (anchorLocal !== undefined) {
    const anchor = parseLocal(anchorLocal);
    if (out.until !== undefined && out.until < anchor.date) invalid('ngày kết thúc trước ngày bắt đầu');
    if (firstOccurrenceAfter(anchorLocal, out, new Date(localToInstant(anchorLocal).getTime() - 1)) === null) {
      invalid('lịch lặp này không có lần nhắc nào');
    }
  }
  return out;
}

type Series = { anchor: Local; r: Recurrence; monday: ISODate };

function series(anchorLocal: string, recurrence: Recurrence): Series {
  const anchor = parseLocal(anchorLocal);
  const r = validateRecurrence(recurrence);
  return { anchor, r, monday: addDays(anchor.date, 1 - isoWeekday(anchor.date)) };
}

function monthOffset(date: ISODate, months: number): { year: number; month: number } {
  const total = Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1 + months;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}

function ymd(year: number, month: number, day: number): ISODate {
  return parseDate(`${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}

/** The earliest local day period k could hold — past `until` means the series has ended. */
function periodStart(s: Series, k: number): ISODate {
  const step = k * s.r.interval;
  if (s.r.freq === 'daily') return addDays(s.anchor.date, step);
  if (s.r.freq === 'weekly') return addDays(s.monday, step * 7);
  const { year, month } = monthOffset(s.anchor.date, step);
  return ymd(year, month, 1);
}

/** The local days of period k, ascending: on or after the anchor day, on or before `until`. */
function periodDays(s: Series, k: number): ISODate[] {
  const step = k * s.r.interval;
  let days: ISODate[];
  if (s.r.freq === 'daily') {
    const day = addDays(s.anchor.date, step);
    days = s.r.workdaysOnly && !isWorkday(day) ? [] : [day];
  } else if (s.r.freq === 'weekly') {
    const start = addDays(s.monday, step * 7);
    days = (s.r.byWeekday ?? [isoWeekday(s.anchor.date)]).map((wd) => addDays(start, wd - 1));
  } else {
    const { year, month } = monthOffset(s.anchor.date, step);
    days = [ymd(year, month, Math.min(Number(s.anchor.date.slice(8, 10)), daysInMonth(year, month)))];
  }
  const until = s.r.until;
  return days.filter((d) => d >= s.anchor.date && (until === undefined || d <= until));
}

/** The period holding local day `day` (≥ the anchor day). */
function periodOf(s: Series, day: ISODate): number {
  if (s.r.freq === 'daily') return Math.floor(daysBetween(s.anchor.date, day) / s.r.interval);
  if (s.r.freq === 'weekly') return Math.floor(daysBetween(s.monday, day) / 7 / s.r.interval);
  const months = (Number(day.slice(0, 4)) - Number(s.anchor.date.slice(0, 4))) * 12
    + Number(day.slice(5, 7)) - Number(s.anchor.date.slice(5, 7));
  return Math.floor(months / s.r.interval);
}

function ended(s: Series, k: number): boolean {
  return s.r.until !== undefined && periodStart(s, k) > s.r.until;
}

function instant(s: Series, day: ISODate): Date {
  return businessInstant(day, s.anchor.time);
}

/** The n-th occurrence (n = 0 is the first), or null when the series ends before it. No recurrence: the anchor only. */
export function occurrenceAt(anchorLocal: string, recurrence: Recurrence | null, n: number): Date | null {
  if (!Number.isSafeInteger(n) || n < 0) throw new DomainRuleError('INVALID_RECURRENCE', `số thứ tự lần nhắc không hợp lệ: ${String(n)}`);
  if (recurrence === null) return n === 0 ? localToInstant(anchorLocal) : null;
  const s = series(anchorLocal, recurrence);
  let left = n;
  for (let k = 0, empty = 0; !ended(s, k) && empty <= MAX_EMPTY_PERIODS; k++) {
    const days = periodDays(s, k);
    empty = days.length === 0 ? empty + 1 : 0;
    if (left < days.length) return instant(s, days[left]!);
    left -= days.length;
  }
  return null;
}

/** The first occurrence strictly after `after`, counted from the anchor; null when the series has ended. */
export function firstOccurrenceAfter(anchorLocal: string, recurrence: Recurrence | null, after: Date): Date | null {
  if (recurrence === null) {
    const once = localToInstant(anchorLocal);
    return once > after ? once : null;
  }
  const s = series(anchorLocal, recurrence);
  const day = businessDate(after);
  const start = day <= s.anchor.date ? 0 : Math.max(0, periodOf(s, day) - 1);
  for (let k = start, empty = 0; !ended(s, k) && empty <= MAX_EMPTY_PERIODS; k++) {
    const days = periodDays(s, k);
    empty = days.length === 0 ? empty + 1 : 0;
    for (const d of days) {
      const at = instant(s, d);
      if (at > after) return at;
    }
  }
  return null;
}

/**
 * The most recent occurrence at or before `at`, or null when none has come yet. After a downtime this is the one
 * missed occurrence a reminder still fires (§7.6); every older one is skipped.
 */
export function latestOccurrenceAtOrBefore(anchorLocal: string, recurrence: Recurrence | null, at: Date): Date | null {
  if (recurrence === null) {
    const once = localToInstant(anchorLocal);
    return once <= at ? once : null;
  }
  const s = series(anchorLocal, recurrence);
  let day = businessDate(at);
  if (s.r.until !== undefined && day > s.r.until) day = s.r.until;
  if (day < s.anchor.date) return null;
  // Walking back always ends at period 0; a live series has an occurrence within a couple of periods.
  for (let k = periodOf(s, day) + 1; k >= 0; k--) {
    for (const d of periodDays(s, k).reverse()) {
      const when = instant(s, d);
      if (when <= at) return when;
    }
  }
  return null;
}

// The words a schedule row shows (L2 D74): the page, chat and every preview read them from here, never a copy.

/** ISO weekday 1 = Monday … 7 = Sunday, in lower case: "thứ 2" … "thứ 7", "chủ nhật". */
export function weekdayWord(isoDay: number): string {
  return isoDay === 7 ? 'chủ nhật' : `thứ ${isoDay + 1}`;
}

const dayMonth = (date: ISODate): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;
const capitalised = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** An instant at HCM time: "Thứ 6, 02/10 lúc 09:00". */
export function describeOccurrence(at: Date): string {
  const { date, time } = parseLocal(instantToLocal(at));
  return `${capitalised(weekdayWord(isoWeekday(date)))}, ${dayMonth(date)} lúc ${time}`;
}

/**
 * A repeat in words: "Hằng ngày", "Các ngày làm việc", "Hằng tuần vào thứ 2, thứ 4", "Hằng tháng ngày 31", each
 * followed by ", đến hết {dd/mm/yyyy}" when it ends. A weekly repeat with no days named falls on the anchor's weekday,
 * and a monthly one on the anchor's day of the month. An interval above one reads "Mỗi {n} ngày/tuần/tháng".
 */
export function describeRecurrence(anchorLocal: string, recurrence: Recurrence): string {
  const anchor = parseLocal(anchorLocal);
  const r = validateRecurrence(recurrence);
  const every = (unit: string, plain: string) => (r.interval === 1 ? plain : `Mỗi ${r.interval} ${unit}`);
  let text: string;
  if (r.freq === 'daily') {
    text = r.interval === 1 && r.workdaysOnly ? 'Các ngày làm việc'
      : `${every('ngày', 'Hằng ngày')}${r.workdaysOnly ? ', chỉ ngày làm việc' : ''}`;
  } else if (r.freq === 'weekly') {
    const days = r.byWeekday ?? [isoWeekday(anchor.date)];
    text = `${every('tuần', 'Hằng tuần')} vào ${days.map(weekdayWord).join(', ')}`;
  } else {
    text = `${every('tháng', 'Hằng tháng')} ngày ${Number(anchor.date.slice(8, 10))}`;
  }
  return r.until === undefined ? text : `${text}, đến hết ${dayMonth(r.until)}/${r.until.slice(0, 4)}`;
}
